import { useCallback, useRef } from "react";
import { notification } from "antd";
import { fetchCurrentUser } from "../services/authAPI";
import {
  resolvePostLoginPath,
  clearOAuthStoredRedirect,
  storePendingOAuthTerms,
} from "../utils/postLoginRedirect";

/**
 * Stable OAuth/social callback handler: uses refs for `login` / `navigate` so the
 * token `useEffect` does not re-run when AuthProvider recreates `login` each render.
 * If the user has not yet accepted terms, the token is not committed to localStorage;
 * the login page shows a one-step terms gate (`?oauth_consent=1`).
 */
export function useTokenLoginHandler(
  login,
  navigate,
  setLoading,
  setProcessingToken
) {
  const loginRef = useRef(login);
  const navigateRef = useRef(navigate);
  loginRef.current = login;
  navigateRef.current = navigate;

  return useCallback(
    async (token, loc) => {
      setLoading(true);
      try {
        localStorage.setItem("token", token);
        const user = await fetchCurrentUser();

        if (!user.terms_accepted_at) {
          localStorage.removeItem("token");
          storePendingOAuthTerms(user, token);
          const next = resolvePostLoginPath(loc);
          const consentUrl = next
            ? `/login?oauth_consent=1&redirect=${encodeURIComponent(next)}`
            : "/login?oauth_consent=1";
          navigateRef.current(consentUrl, { replace: true });
          return;
        }

        loginRef.current(user, token);
        localStorage.removeItem("login_type");
        const next = resolvePostLoginPath(loc);
        clearOAuthStoredRedirect();
        navigateRef.current(next || "/submittals", { replace: true });
      } catch (error) {
        clearOAuthStoredRedirect();
        localStorage.removeItem("token");
        console.error("Token Login Error:", error);
        notification.error({
          message: "Social Login Failed",
          description: "Could not retrieve user details.",
        });
      } finally {
        setProcessingToken(false);
        setLoading(false);
      }
    },
    [setLoading, setProcessingToken]
  );
}
